import React, { useState, useEffect } from 'react';
import { UserPlus, Trash2 } from 'lucide-react';
import C from '../../constants/colors';
import { list as listEmployees, setHseProfiler } from '../../api/employees';

export default function TeamView({ companyId, sites = [] }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!companyId) return;
    listEmployees({ companyId })
      .then(data => setEmployees(data || []))
      .finally(() => setLoading(false));
  }, [companyId]);

  const team = employees.filter(e => e.hseProfiler);

  async function handleRemove(emp) {
    setError('');
    try {
      await setHseProfiler(emp.id, false);
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, hseProfiler: false } : e));
    } catch {
      setError(`Failed to remove ${emp.name} from HSE team — please try again.`);
    }
  }

  async function handleAdd(emp) {
    setError('');
    try {
      await setHseProfiler(emp.id, true);
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, hseProfiler: true } : e));
      setAddModal(false);
    } catch {
      setError(`Failed to add ${emp.name} to HSE team — please try again.`);
    }
  }

  if (loading) return <div style={{ padding: '32px', color: C.textMuted }}>Loading…</div>;

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ color: C.textPrimary, fontSize: '18px', fontWeight: 700, margin: 0 }}>HSE Team</h2>
          <p style={{ color: C.textMuted, fontSize: '12px', margin: '4px 0 0' }}>
            Members can scan employee badges on the HSE mobile page.
          </p>
        </div>
        <button
          onClick={() => setAddModal(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: C.blue, color: '#fff', border: 'none', borderRadius: '7px', padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          <UserPlus size={14} /> Add Member
        </button>
      </div>

      {error && <div style={{ color: C.red, fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

      {team.length === 0 ? (
        <div style={{ color: C.textMuted, fontSize: '13px', padding: '32px 0', textAlign: 'center' }}>
          No HSE team members yet. Click "Add Member" to get started.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ color: C.textMuted, borderBottom: `1px solid ${C.border}` }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600 }}>Name</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600 }}>Employee ID</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600 }}>Department</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600 }}>Site</th>
              <th style={{ padding: '8px 10px' }} />
            </tr>
          </thead>
          <tbody>
            {team.map(emp => {
              const site = sites.find(s => s.id === emp.homeSiteId);
              return (
                <tr key={emp.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '10px 10px', color: C.textPrimary, fontWeight: 600 }}>{emp.name}</td>
                  <td style={{ padding: '10px 10px', color: C.textSecondary }}>{emp.employeeId}</td>
                  <td style={{ padding: '10px 10px', color: C.textSecondary }}>{emp.department || '—'}</td>
                  <td style={{ padding: '10px 10px', color: C.textSecondary }}>{site?.name || '—'}</td>
                  <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                    <button
                      onClick={() => handleRemove(emp)}
                      style={{ background: 'none', border: `1px solid ${C.border}`, color: C.red, borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {addModal && (
        <AddMemberModal
          companyId={companyId}
          sites={sites}
          currentTeamIds={new Set(team.map(e => e.id))}
          onAdd={handleAdd}
          onClose={() => setAddModal(false)}
        />
      )}
    </div>
  );
}

function AddMemberModal({ companyId, sites, currentTeamIds, onAdd, onClose }) {
  const [siteId, setSiteId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);

  useEffect(() => {
    if (!siteId) { setEmployees([]); return; }
    setLoadingEmps(true);
    listEmployees({ companyId })
      .then(data => setEmployees(data || []))
      .finally(() => setLoadingEmps(false));
  }, [siteId, companyId]);

  const candidates = employees.filter(e =>
    e.homeSiteId === siteId &&
    e.department?.toLowerCase() === 'hse' &&
    !currentTeamIds.has(e.id)
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px', width: '420px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ color: C.textPrimary, margin: '0 0 16px', fontSize: '15px' }}>Add HSE Team Member</h3>

        <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Select Site</label>
        <select
          value={siteId}
          onChange={e => setSiteId(e.target.value)}
          style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '8px 10px', color: siteId ? C.textPrimary : C.textMuted, fontSize: '13px', marginBottom: '16px', fontFamily: 'inherit' }}
        >
          <option value="">— Choose a site —</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        {siteId && (
          <>
            <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>HSE Department Employees</label>
            <div style={{ overflow: 'auto', flex: 1, marginBottom: '14px' }}>
              {loadingEmps ? (
                <div style={{ color: C.textMuted, fontSize: '13px', padding: '16px 0', textAlign: 'center' }}>Loading…</div>
              ) : candidates.length === 0 ? (
                <div style={{ color: C.textMuted, fontSize: '13px', padding: '16px 0', textAlign: 'center' }}>
                  No HSE department employees found at this site.
                </div>
              ) : (
                candidates.map(emp => (
                  <div
                    key={emp.id}
                    onClick={() => onAdd(emp)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: '7px', marginBottom: '4px', background: C.surface + '88', cursor: 'pointer' }}
                  >
                    <div>
                      <div style={{ color: C.textPrimary, fontSize: '13px', fontWeight: 600 }}>{emp.name}</div>
                      <div style={{ color: C.textMuted, fontSize: '11px' }}>{emp.employeeId}</div>
                    </div>
                    <span style={{ color: C.blue, fontSize: '12px', fontWeight: 600 }}>Add →</span>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSecondary, borderRadius: '6px', padding: '7px 16px', fontSize: '13px', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}
